/**
 * The portfolio as the page shows it: every account the user has — live from the brokers that
 * are connected, plus holdings recorded by hand for anything kept elsewhere — in one picture.
 */
import type { CopyView } from './copy'

export interface PfAccount {
  id: string
  broker: 'robinhood' | 'ibkr' | 'manual'
  /** "Robinhood", "Robinhood · Agentic", "Interactive Brokers", "Elsewhere". */
  name: string
  /** Last digits of the account number (never the whole number). */
  mask: string | null
  /** Orders can be sent to it from here. */
  tradable: boolean
  paper: boolean
  /** Its prices and positions come live from the broker. */
  live: boolean
  value: number
  cash: number | null
  buyingPower: number | null
  dayPnl: number | null
  openPnl: number | null
  /** Value held outside stocks, as the broker totals it (options, crypto …) — counted in `value`, not listed. */
  other: { label: string; value: number }[]
  /** Why orders are blocked, when they are. */
  blocked: string | null
  updatedAt: number
}

export interface PfHoldingPart {
  account: string
  quantity: number
  avgCost: number | null
  value: number | null
  openPnl: number | null
  dayPnl: number | null
  /** Running trade workers holding part of these shares. */
  workers: { id: string; name: string; open: number }[]
  /** Of `quantity`, shares that belong to the user's copies (their own shares = quantity − copyQty). */
  copyQty?: number
}

export interface PfHolding {
  /** "GOOGL" for display; `code` is the app-wide symbol ("GOOGL.US"). */
  symbol: string
  code: string
  quantity: number
  avgCost: number | null
  last: number | null
  prevClose: number | null
  value: number | null
  cost: number | null
  openPnl: number | null
  openPnlPct: number | null
  dayPnl: number | null
  dayPct: number | null
  /** Share of the shown total (percent). */
  weight: number
  stopLoss: number | null
  takeProfit: number | null
  parts: PfHoldingPart[]
  /** Of `quantity`, shares that belong to the user's copies (their own shares = quantity − copyQty). */
  copyQty?: number
}

export interface PfOrder {
  id: string
  account: string
  symbol: string
  side: 'BUY' | 'SELL'
  /** MKT, LMT, STP, STP LMT, TRAIL … */
  type: string
  quantity: number | null
  filled: number
  limit: number | null
  stop: number | null
  tif: string | null
  status: string
  /** Placed by a trade worker / a chat order of this app. */
  workerId: string | null
  ticketId: string | null
  /** The ticket was drawn in a chat (by the AI); false = filled in by hand on the order form. */
  fromChat?: boolean
  /** Cancellable from here (the app's own order id); null = placed elsewhere (e.g. in the broker's app). */
  orderId: number | null
  at: number | null
}

export interface PfFill {
  id: string
  account: string
  at: number
  symbol: string
  side: 'BUY' | 'SELL'
  shares: number
  price: number
  /** 'worker' | 'order' (a chat order) | 'app' (this app's ticket) | 'broker' (placed at the broker) | 'recorded' (by hand). */
  source: 'worker' | 'order' | 'app' | 'broker' | 'recorded'
  workerId: string | null
  today: boolean
}

export interface PortfolioLive {
  accounts: PfAccount[]
  holdings: PfHolding[]
  orders: PfOrder[]
  fills: PfFill[]
  totals: { value: number; cash: number; cost: number; openPnl: number; openPnlPct: number; dayPnl: number; dayPct: number }
  /** 'pre' | 'regular' | 'after' | 'closed' (US market). */
  session: string
  /** At least one broker feeds this live. */
  live: boolean
  updatedAt: number
  /** The user's running copies: sub-accounts inside their brokerage account (their shares are part of `holdings`). */
  copies?: CopyView[]
}

/** The same picture for one account (or all, when `account` is null): holdings re-weighted, totals re-summed. */
export function scopePortfolio(p: PortfolioLive, account: string | null): PortfolioLive {
  if (!account) return p
  const accounts = p.accounts.filter((a) => a.id === account)
  const a = accounts[0]
  if (!a) return p
  const rows = p.holdings
    .map((h) => {
      const part = h.parts.find((x) => x.account === account)
      if (!part) return null
      const cost = part.avgCost != null ? part.avgCost * part.quantity : null
      const base = part.value != null && part.dayPnl != null ? part.value - part.dayPnl : null
      return {
        ...h,
        quantity: part.quantity,
        avgCost: part.avgCost,
        value: part.value,
        cost,
        openPnl: part.openPnl,
        openPnlPct: part.openPnl != null && cost ? (part.openPnl / cost) * 100 : null,
        dayPnl: part.dayPnl,
        dayPct: part.dayPnl != null && base ? (part.dayPnl / base) * 100 : null,
        parts: [part],
        copyQty: part.copyQty,
      } as PfHolding
    })
    .filter((h): h is PfHolding => !!h)
  const total = rows.reduce((s, h) => s + (h.value ?? 0), 0)
  const holdings = rows.map((h) => ({ ...h, weight: total > 0 ? ((h.value ?? 0) / total) * 100 : 0 }))
  const cost = holdings.reduce((s, h) => s + (h.cost ?? 0), 0)
  const openPnl = a.openPnl ?? 0
  const dayPnl = a.dayPnl ?? 0
  return {
    ...p,
    accounts,
    holdings,
    orders: p.orders.filter((o) => o.account === account),
    fills: p.fills.filter((f) => f.account === account),
    totals: {
      value: a.value,
      cash: a.cash ?? 0,
      cost,
      openPnl,
      openPnlPct: cost > 0 ? (openPnl / cost) * 100 : 0,
      dayPnl,
      dayPct: a.value - dayPnl > 0 ? (dayPnl / (a.value - dayPnl)) * 100 : 0,
    },
  }
}

/**
 * A value history with the money that was added or taken out removed, so the line shows what the
 * holdings did — not a deposit as a gain. A step is a deposit / withdrawal when the capital behind
 * the portfolio (`cost`) and its value jump together by about the same amount.
 * Returns each point's value shifted so the series ENDS at the real current value, plus the totals.
 */
export function performanceSeries(points: { ts: number; value: number; cost: number }[]): { series: { ts: number; value: number }[]; added: number; gain: number; gainPct: number } {
  if (!points.length) return { series: [], added: 0, gain: 0, gainPct: 0 }
  const flows: number[] = [0]
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]
    const b = points[i]
    const dC = b.cost - a.cost
    const dV = b.value - a.value
    const big = Math.abs(dC) > Math.max(1, Math.abs(a.value) * 0.002)
    const together = Math.sign(dC) === Math.sign(dV) && Math.abs(dV - dC) <= Math.max(2, Math.abs(dC) * 0.25, Math.abs(a.value) * 0.015)
    flows.push(big && together ? dC : 0)
  }
  const added = flows.reduce((s, f) => s + f, 0)
  let cum = 0
  const series = points.map((p, i) => {
    cum += flows[i]
    return { ts: p.ts, value: p.value - cum + added }
  })
  const gain = points[points.length - 1].value - points[0].value - added
  // Against the money that was at work: what was there at the start plus what was added.
  const base = points[0].value + Math.max(0, added)
  return { series, added, gain, gainPct: base > 0 ? (gain / base) * 100 : 0 }
}
